import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import PasswordResetService from '../../services/passwordReset.service';

// Brand colors extracted from the Techno logo
const BRAND = {
  dark: '#0d0d0d',
  charcoal: '#1a1a1a',
  surface: '#242424',
  bar1: '#6abf69', // green
  bar2: '#f0a500', // orange
  bar3: '#4a9fd4', // blue
  bar4: '#3a7d44', // dark green
  text: '#f0f0f0',
  muted: '#888',
};

function TechnoLogo({ size = 'lg' }) {
  const isLg = size === 'lg';
  const bracket = isLg ? 48 : 28;
  const name = isLg ? 32 : 20;
  const barH = isLg ? 5 : 3;
  const barGap = isLg ? 6 : 4;
  const barW = isLg ? 56 : 36;
  const mt = isLg ? 14 : 8;

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', userSelect: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Opening bracket */}
        <Typography
          sx={{
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: bracket,
            fontWeight: 700,
            color: BRAND.text,
            lineHeight: 1,
            mt: '-4px',
          }}
        >
          {'}'}
        </Typography>

        {/* Brand name */}
        <Box sx={{ position: 'relative' }}>
          <Typography
            sx={{
              fontFamily: '"Courier New", Courier, monospace',
              fontSize: name,
              fontWeight: 700,
              color: BRAND.text,
              letterSpacing: isLg ? 3 : 2,
              lineHeight: 1,
            }}
          >
            TECHN
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isLg ? 28 : 18,
                height: isLg ? 28 : 18,
                border: `${isLg ? 3 : 2}px solid ${BRAND.text}`,
                borderRadius: '50%',
                position: 'relative',
                mx: '1px',
                verticalAlign: 'middle',
                mb: isLg ? '3px' : '2px',
              }}
            >
              <Box
                sx={{
                  width: isLg ? 6 : 4,
                  height: isLg ? 6 : 4,
                  borderRadius: '50%',
                  bgcolor: BRAND.text,
                }}
              />
            </Box>
            OPTION
          </Typography>
        </Box>

        {/* Closing semicolon */}
        <Typography
          sx={{
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: bracket,
            fontWeight: 700,
            color: BRAND.muted,
            lineHeight: 1,
            mt: '-4px',
          }}
        >
          {';'}
        </Typography>
      </Box>

      {/* Colored bars */}
      <Box sx={{ display: 'flex', gap: `${barGap}px`, mt: `${mt}px` }}>
        {[BRAND.bar1, BRAND.bar2, BRAND.bar3, BRAND.bar4].map((color) => (
          <Box
            key={color}
            sx={{ width: barW, height: barH, borderRadius: 1, bgcolor: color }}
          />
        ))}
      </Box>
    </Box>
  );
}

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fafafa',
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#bdbdbd' },
    '&.Mui-focused fieldset': { borderColor: '#1a1a1a', borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1a1a1a' },
};

const Login = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    try {
      await PasswordResetService.forgotPassword(forgotEmail);
      setForgotMessage(t('login.passwordResetSuccess'));
    } catch (err) {
      setForgotMessage(err.response?.data?.message || t('login.passwordResetError'));
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgot = () => {
    setForgotOpen(true);
    setForgotEmail(formData.email);
    setForgotMessage('');
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setForgotEmail('');
    setForgotMessage('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: '#f5f5f7',
      }}
    >
      {/* ── Left brand panel ── */}
      <Box
        sx={{
          width: { xs: '100%', md: '45%' },
          minHeight: { xs: 'auto', md: '100vh' },
          bgcolor: BRAND.dark,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'space-between' },
          px: { xs: 3, md: 6 },
          py: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative code lines background */}
        {!isMobile && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: 0.04,
              fontFamily: '"Courier New", monospace',
              fontSize: 13,
              color: '#fff',
              p: 4,
              lineHeight: 2.2,
              pointerEvents: 'none',
              whiteSpace: 'pre',
              overflow: 'hidden',
            }}
          >
            {`const system = {
  name: "Techno",
  version: "2.0",
  modules: [
    "students",
    "invoices",
    "attendance",
    "reports",
    "clients",
  ],
};

function init(tenant) {
  return system
    .connect(tenant)
    .authenticate();
}`}
          </Box>
        )}

        {/* Top spacer / logo area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 }}>
          <TechnoLogo size={isMobile ? 'sm' : 'lg'} />

          {!isMobile && (
            <Typography
              sx={{
                color: BRAND.muted,
                fontSize: 14,
                mt: 4,
                textAlign: 'center',
                maxWidth: 300,
                lineHeight: 1.7,
                fontFamily: '"Courier New", monospace',
              }}
            >
              Sistema de gestión empresarial moderno para equipos que mueven empresas.
            </Typography>
          )}
        </Box>

        {/* Bottom feature pills — desktop only */}
        {!isMobile && (
          <Box sx={{ zIndex: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
              {['Facturación', 'Alumnos', 'Asistencia', 'Reportes', 'Inventario'].map((f, i) => {
                const colors = [BRAND.bar1, BRAND.bar2, BRAND.bar3, BRAND.bar4, BRAND.bar2];
                return (
                  <Box
                    key={f}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 99,
                      border: `1px solid ${colors[i]}33`,
                      bgcolor: `${colors[i]}18`,
                    }}
                  >
                    <Typography sx={{ color: colors[i], fontSize: 11, fontWeight: 600 }}>{f}</Typography>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ color: '#444', fontSize: 11, textAlign: 'center' }}>
              © {new Date().getFullYear()} Techno · Todos los derechos reservados
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Right form panel ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 4, md: 0 },
          bgcolor: '#ffffff',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Greeting */}
          <Typography variant="h5" fontWeight={700} color="#0d0d0d" mb={0.5}>
            Bienvenido de vuelta
          </Typography>
          <Typography variant="body2" color="#888" mb={4}>
            Ingresa tus credenciales para continuar
          </Typography>

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              name="email"
              label={t('login.emailAddress')}
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={FIELD_SX}
            />

            <TextField
              fullWidth
              name="password"
              label={t('login.password')}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" size="small">
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 20, color: '#bbb' }} />
                      ) : (
                        <Visibility sx={{ fontSize: 20, color: '#bbb' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={FIELD_SX}
            />

            {/* Forgot password link */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={openForgot}
                sx={{ color: '#888', textDecoration: 'none', '&:hover': { color: BRAND.dark } }}
              >
                {t('login.forgotPassword')}
              </Link>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: 13 }}>
                {error}
              </Alert>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              endIcon={!loading && <ArrowIcon />}
              sx={{
                mt: 3,
                py: 1.6,
                borderRadius: 2,
                bgcolor: BRAND.dark,
                color: '#fff',
                fontWeight: 600,
                fontSize: 15,
                letterSpacing: 0.3,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#2a2a2a', boxShadow: 'none' },
                '&:disabled': { bgcolor: '#ccc' },
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t('login.signIn')}
            </Button>
          </form>

          {/* Accent bars at bottom */}
          <Box sx={{ display: 'flex', gap: '6px', mt: 5, justifyContent: 'center' }}>
            {[BRAND.bar1, BRAND.bar2, BRAND.bar3, BRAND.bar4].map((c) => (
              <Box key={c} sx={{ width: 32, height: 3, borderRadius: 1, bgcolor: c }} />
            ))}
          </Box>

          {isMobile && (
            <Typography sx={{ color: '#ccc', fontSize: 11, textAlign: 'center', mt: 2 }}>
              © {new Date().getFullYear()} Techno · Todos los derechos reservados
            </Typography>
          )}
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onClose={closeForgot} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('login.resetPassword')}</DialogTitle>
        <form onSubmit={handleForgotPassword}>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('login.enterEmailMessage')}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label={t('login.emailAddress')}
              type="email"
              fullWidth
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: '#bbb', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={FIELD_SX}
            />
            {forgotMessage && (
              <Alert
                severity={forgotMessage.includes('sent') || forgotMessage.includes('enviado') ? 'success' : 'error'}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                {forgotMessage}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={closeForgot} disabled={forgotLoading} sx={{ color: '#666', textTransform: 'none' }}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={forgotLoading || !forgotEmail}
              sx={{
                bgcolor: BRAND.dark,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                borderRadius: 2,
                '&:hover': { bgcolor: '#2a2a2a', boxShadow: 'none' },
              }}
            >
              {forgotLoading ? <CircularProgress size={18} color="inherit" /> : t('login.sendResetLink')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Login;
